import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormGroup from "@mui/material/FormGroup";
import Skeleton from "@mui/material/Skeleton";
import Typography from "@mui/material/Typography";
import makeStyles from "@mui/styles/makeStyles";

import { Dialog } from "primereact/dialog";

import { useAssetById } from "../../data/Assets/useAssetById";
import { usePanelsBySwitchgear } from "../../data/Assets/usePanelsBySwitchgear";
import { CancelButton } from "../../shared/CancelButton";
import { MessageToast, ToastMode } from "../../shared/MessageToast";
import { ValidationMessage } from "../../shared/ValidationMessage";
import { managedSurveyAssetType } from "../../shared/constants/managedSurveyAssetTypes";
import { getSubAssetType } from "../../shared/interfaces/SurveyableAsset";
import { Panel } from "../../shared/interfaces/Switchgear";
import type { GroupedPanel } from "../workflowReducer";

interface Props {
  switchgearId?: number;
  open: boolean;
  onCancelClick: () => void;
  onChangePanelsClick: (panels: GroupedPanel[]) => void;
  excludePanels?: GroupedPanel[];
}

const useStyles = makeStyles((theme) => ({
  dialogContentContainer: {
    paddingBottom: "0px !important",
    paddingTop: "1em !important",
  },
  formControl: {
    margin: theme.spacing(2),
  },
  panelNo: {
    margin: theme.spacing(0, 1),
    fontSize: "1.1em",
  },
  footerButton: {
    marginLeft: theme.spacing(1),
  },
}));

interface CheckboxItem {
  panel: GroupedPanel;
  checked?: boolean;
}

export function PanelMeasurementsAdd(props: Props) {
  const { t } = useTranslation();
  const classes = useStyles();

  const asset = useAssetById(props.switchgearId).data;
  const subAssetType = getSubAssetType(asset?.assetTypeKey || managedSurveyAssetType.Switchgear);

  const panelData = usePanelsBySwitchgear(props.switchgearId, {
    onSuccess: onFetchPanelsSuccess,
  });

  const [panelCheckboxes, setPanelCheckboxes] = useState<CheckboxItem[]>([]);

  useEffect(() => {
    if (props.open) {
      const checkboxes = panelData.data
        ?.sort((a, b) => a.panelNo?.localeCompare(b.panelNo, undefined, { numeric: true }))
        .filter((panel) => !props.excludePanels?.some((p) => p.panelNo === panel.panelNo))
        .map((panel) => ({ panel: panel, checked: false }) as CheckboxItem);
      setPanelCheckboxes(checkboxes || []);
    }
  }, [props.excludePanels, props.open, panelData.data]);

  function onFetchPanelsSuccess(data: Panel[] | undefined) {
    const checkboxes = data
      ?.sort((a, b) => a.panelNo?.localeCompare(b.panelNo, undefined, { numeric: true }))
      .filter((panel) => !props.excludePanels?.some((p) => p.panelNo === panel.panelNo))
      .map((panel) => ({ panel: panel, checked: false }) as CheckboxItem);
    setPanelCheckboxes(checkboxes || []);
  }

  function handleCheckboxChange(event: React.ChangeEvent<HTMLInputElement>) {
    const { value, checked } = event.target;

    let changedPanels = [...panelCheckboxes];
    let foundPanel = changedPanels?.find((m) => m.panel.panelNo === value);

    if (foundPanel) foundPanel.checked = checked;

    setPanelCheckboxes(changedPanels);
  }

  function setAllPanelsCheckedState(checked: boolean) {
    let changedPanels = [...panelCheckboxes];
    changedPanels.forEach((panel) => (panel.checked = checked));
    setPanelCheckboxes(changedPanels);
  }

  function handleAddPanelsButtonClick() {
    const selectedPanels = panelCheckboxes
      .filter((panelCheckbox) => panelCheckbox.checked)
      .map((panelCheckbox) => panelCheckbox.panel);
    props.onChangePanelsClick(selectedPanels);
  }

  const header = (
    <Typography variant="h5" component="p">{`${t("panels.selectPanels", { val: subAssetType })} `}</Typography>
  );

  const footer = (
    <>
      <Button
        className={classes.footerButton}
        variant="contained"
        color="primary"
        onClick={handleAddPanelsButtonClick}
        disabled={panelCheckboxes?.length === 0}
      >
        {t("panels.addPanels", { val: subAssetType })}
      </Button>
      <CancelButton className={classes.footerButton} onClick={props.onCancelClick} />
    </>
  );

  return (
    <>
      <Dialog
        header={header}
        visible={props.open}
        onHide={props.onCancelClick}
        footer={footer}
        contentClassName={classes.dialogContentContainer}
        baseZIndex={9999}
      >
        {panelData.isLoading ? (
          <>
            <Skeleton />
            <Skeleton />
            <Skeleton />
          </>
        ) : panelCheckboxes?.length === 0 ? (
          <Typography variant="body2" component="span">
            {!props.switchgearId ? (
              <ValidationMessage invalidMessage={t("workflows.selectSwitchgear")} validationType="warning" showIcon />
            ) : (
              t("panels.allPanelsAdded", { val: subAssetType })
            )}
          </Typography>
        ) : (
          <>
            <div>
              <Button size="small" color="primary" onClick={() => setAllPanelsCheckedState(true)}>
                {t("common.selectAll")}
              </Button>
              {"|"}
              <Button size="small" color="primary" onClick={() => setAllPanelsCheckedState(false)}>
                {t("common.clearSelection")}
              </Button>
            </div>
            <FormControl component="fieldset" className={classes.formControl}>
              <FormGroup>
                {panelCheckboxes &&
                  panelCheckboxes.length > 0 &&
                  panelCheckboxes?.map((checkboxItem) => (
                    <FormControlLabel
                      key={`checkboxLabel-${checkboxItem.panel.name}-${checkboxItem.panel.panelNo}`}
                      label={
                        <>
                          <Typography variant="subtitle2" component="span" className={classes.panelNo}>
                            {checkboxItem.panel.panelNo}
                          </Typography>
                          {"- "}
                          <Typography variant="body2" component="span">
                            {checkboxItem.panel.name}
                          </Typography>
                        </>
                      }
                      control={
                        <Checkbox
                          key={`checkbox-${checkboxItem.panel.name}-${checkboxItem.panel.panelNo}`}
                          checked={checkboxItem.checked}
                          onChange={handleCheckboxChange}
                          color="primary"
                          value={checkboxItem.panel.panelNo}
                        />
                      }
                    />
                  ))}
              </FormGroup>
            </FormControl>
          </>
        )}
        <MessageToast
          text={t("common.dataFetchError", { val: "$t(panels.panels)" })}
          mode={ToastMode.Error}
          show={panelData.isError && !panelData.data}
        />
      </Dialog>
    </>
  );
}
